import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useNavigate, useLocation } from "react-router-dom";
import { HelpCircle, Search, X, ChevronRight, BookOpen, Sparkles, Send, ArrowLeft } from "lucide-react";

/** Floating "?" button — bottom-right of every /app/* screen. Two modes:
 *  1. Search — live search across all Help articles + contextual for current page
 *  2. Ask AI — free-form question answered by Zenith360 AI (Gemini + article RAG) */
export default function FloatingHelpButton() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("search"); // search | ask
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [contextual, setContextual] = useState([]);
  const [aiQ, setAiQ] = useState("");
  const [aiAns, setAiAns] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  // Infer module from URL path
  const module = useMemo(() => {
    const p = loc.pathname;
    const map = [
      [/\/sales|\/customers|\/credit-notes|\/recurring/, "sales"],
      [/\/purchase|\/vendors|\/debit-notes|\/approvals/, "purchase"],
      [/\/inventory|\/items|\/warehouses|\/stock/, "inventory"],
      [/\/bom|\/production|\/mrp|\/quality|\/costing|\/npd|\/mfg-masters|\/shopfloor|\/andon/, "manufacturing"],
      [/\/banking/, "banking"],
      [/\/gate-pass/, "gate_pass"],
      [/\/compliance|\/gst|\/tds|\/itr|\/mca|\/tax-audit/, "compliance"],
      [/\/hr|\/employees|\/payroll|\/attendance|\/leave/, "hr"],
      [/\/zoho|\/tally|\/import/, "migration"],
      [/\/reports|\/consolidation|\/segments/, "reports"],
      [/\/settings|\/brochure/, "brochure"],
    ];
    for (const [rx, mod] of map) if (rx.test(p)) return mod;
    return null;
  }, [loc.pathname]);

  useEffect(() => {
    if (!open) return;
    if (module) {
      api.get(`/help/articles`, { params: { module } }).then((r) => setContextual(r.data.slice(0, 5)));
    }
  }, [open, module]);

  useEffect(() => {
    if (!q.trim() || q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/help/search", { params: { q } }).then((r) => setResults(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (slug) => { setOpen(false); setQ(""); nav(`/app/help/${slug}`); };

  const askAI = async (overrideQ) => {
    const question = (typeof overrideQ === "string" ? overrideQ : aiQ).trim();
    if (question.length < 3) return;
    setAiQ(question);
    setAiBusy(true); setAiAns(null);
    try {
      const r = await api.post("/help/ask", { question, module });
      setAiAns(r.data);
    } catch (e) {
      setAiAns({ answer: `**Error**: ${e?.response?.data?.detail || "AI is unavailable right now."}`, cited_slugs: [], fallback: true });
    } finally { setAiBusy(false); }
  };
  const renderAns = (t) => t
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 rounded-sm text-[11px] font-mono">$1</code>')
    .replace(/\[([a-z0-9-]+)\]/g, '<a href="/app/help/$1" class="text-accent underline">$1</a>')
    .replace(/\n/g, "<br/>");

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="floating-help"
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-accent hover:bg-accent/90 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        aria-label="Help">
        <HelpCircle className="w-6 h-6" />
        <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-black text-white text-[11px] font-mono px-2 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">Help &amp; Docs</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 bg-black/50 animate-in fade-in" onClick={() => setOpen(false)} data-testid="help-overlay">
          <div className="w-full max-w-2xl bg-white rounded-sm shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Mode tabs */}
            <div className="flex border-b bg-muted/20">
              <button onClick={() => setMode("search")} className={`flex-1 p-2 text-xs font-mono uppercase tracking-widest transition-colors ${mode === "search" ? "bg-white text-accent border-b-2 border-accent" : "text-muted-foreground hover:text-foreground"}`} data-testid="help-tab-search">
                <Search className="w-3 h-3 inline mr-1" /> Search Docs
              </button>
              <button onClick={() => setMode("ask")} className={`flex-1 p-2 text-xs font-mono uppercase tracking-widest transition-colors ${mode === "ask" ? "bg-white text-accent border-b-2 border-accent" : "text-muted-foreground hover:text-foreground"}`} data-testid="help-tab-ask">
                <Sparkles className="w-3 h-3 inline mr-1" /> Ask Zenith360 AI
              </button>
              <button onClick={() => setOpen(false)} className="px-3 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>

            {mode === "search" ? (
              <>
                <div className="flex items-center gap-2 p-3 border-b">
                  <Search className="w-4 h-4 text-accent" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help — BOM, ITR, GSTR-1, DSC, TDS…" className="flex-1 outline-none text-sm" autoFocus data-testid="help-overlay-input" />
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {q.trim().length >= 2 ? (
                    results.length === 0 ? (
                      <div className="p-6 text-center text-muted-foreground text-sm">
                        No matches for "{q}" — try <button onClick={() => { setMode("ask"); setAiQ(q); }} className="text-accent underline">Ask Zenith360 AI</button>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {results.map((r) => (
                          <button key={r.slug} onClick={() => go(r.slug)} className="w-full text-left p-3 hover:bg-accent/5 flex items-start gap-3 group" data-testid={`help-result-${r.slug}`}>
                            <BookOpen className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm group-hover:text-accent">{r.title}</div>
                              <div className="text-[10px] font-mono uppercase text-muted-foreground">{r.module} · {r.category}</div>
                              {r.snippet && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.snippet}</div>}
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                          </button>
                        ))}
                      </div>
                    )
                  ) : (
                    <>
                      {module && contextual.length > 0 && (
                        <div>
                          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/30">On this page ({module})</div>
                          {contextual.map((r) => (
                            <button key={r.slug} onClick={() => go(r.slug)} className="w-full text-left p-3 hover:bg-accent/5 flex items-start gap-3 group border-b">
                              <BookOpen className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                              <div className="flex-1"><div className="font-semibold text-sm group-hover:text-accent">{r.title}</div>
                                <div className="text-[10px] font-mono uppercase text-muted-foreground">{r.category}</div></div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="p-4">
                        <button onClick={() => { setOpen(false); nav("/app/help"); }} className="w-full text-sm text-accent hover:underline text-center" data-testid="help-browse-all">Browse all articles →</button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              /* Ask AI mode */
              <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
                {!aiAns && (
                  <>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3 text-accent" /> Ask me anything about Zenith 360. Answers are grounded in your Help articles.</div>
                    <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                      {["How do I generate an IRN?", "What is Cp / Cpk?", "How to file GSTR-3B?", "How to create a BOM?"].map((s) => (
                        <button key={s} onClick={() => askAI(s)} disabled={aiBusy} className="text-left p-2 rounded-sm border hover:border-accent/40 hover:bg-accent/5 disabled:opacity-40" data-testid={`help-sample-${s.slice(0,10)}`}>{s}</button>
                      ))}
                    </div>
                  </>
                )}
                {aiAns && (
                  <div data-testid="help-ai-answer" className="rounded-sm border border-accent/30 bg-accent/5 p-3">
                    <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-accent mb-2"><Sparkles className="w-3 h-3" /> Zenith360 AI {aiAns.fallback && "(fallback)"}</div>
                    <div className="text-sm leading-6" dangerouslySetInnerHTML={{ __html: renderAns(aiAns.answer) }} />
                    {aiAns.cited_slugs?.length > 0 && (
                      <div className="mt-3 pt-2 border-t text-[10px] flex items-center gap-1 flex-wrap">
                        <span className="font-mono uppercase text-muted-foreground">Sources:</span>
                        {aiAns.cited_slugs.map((s) => (
                          <button key={s} onClick={() => go(s)} className="font-mono text-accent hover:underline">{s}</button>
                        ))}
                      </div>
                    )}
                    <button onClick={() => { setAiAns(null); setAiQ(""); }} className="text-xs text-accent hover:underline mt-3 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Ask another</button>
                  </div>
                )}
                <div className="flex items-center gap-2 border-t pt-3">
                  <input value={aiQ} onChange={(e) => setAiQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && askAI()}
                    placeholder="e.g. how do I close a work order?" className="flex-1 outline-none text-sm border rounded-sm px-3 py-2" autoFocus data-testid="help-ai-input" />
                  <button onClick={askAI} disabled={aiBusy || aiQ.trim().length < 3} className="p-2 rounded-sm bg-accent text-white hover:bg-accent/90 disabled:opacity-40" data-testid="help-ai-send"><Send className="w-4 h-4" /></button>
                </div>
                {aiBusy && <div className="text-xs text-muted-foreground italic animate-pulse">Thinking…</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
